/**
 * impersonation-client.ts — axios instance that targets @mgmt /api/v3 using
 * the CUSTOMER access token from an impersonation session.
 *
 * Model change (implicit): no global "active session". Each org detail page
 * sets the "active org" via setImpersonationOrgId(orgId) on mount; the
 * request interceptor reads the per-org cached token for that org.
 *
 * Key invariants:
 *   - Authorization: Bearer <customer_token>  (NOT the employee RPT)
 *   - NO X-Act-As-Org header (the customer JWT carries the org claim natively)
 *   - 401 response → clears the session for the active org
 *
 * Usage: import { impersonationClient, setImpersonationOrgId } from '@/api/impersonation-client'
 */

import axios, { type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import {
  getImpersonationSessionForOrg,
  clearImpersonationSession,
} from '@/lib/impersonation-session';

/** The org whose customer token this client uses for outgoing requests. */
let _activeOrgId: string = '';

/** Set the org context for impersonation-client. Call on org detail page mount. */
export function setImpersonationOrgId(orgId: string): void {
  _activeOrgId = orgId;
}

/** Get the currently active org id. */
export function getImpersonationOrgId(): string {
  return _activeOrgId;
}

/** Attach the current org's customer access_token from the cached session. */
function withCustomerToken(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (_activeOrgId) {
    const session = getImpersonationSessionForOrg(_activeOrgId);
    if (session) {
      config.headers.set('Authorization', `Bearer ${session.customerToken}`);
    }
  }
  return config;
}

/**
 * Axios instance for @mgmt /api/v3 calls using the customer's own JWT.
 *
 * Interceptor order:
 *   1. Request: attach customer access_token from the active org's session.
 *   2. Response 401: clear the org session (token expired).
 */
export const impersonationClient = axios.create({
  baseURL: env.serviceApiV3Url,
  timeout: 30_000,
});

impersonationClient.interceptors.request.use(withCustomerToken);

impersonationClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 && _activeOrgId) {
      // Customer token expired or revoked — clear only this org's session.
      clearImpersonationSession(_activeOrgId);
    }
    return Promise.reject(error);
  },
);
